import React, { useState, useEffect, useCallback } from 'react';
import { 
    AlertTriangle, Loader2, CheckCircle2, Check, X, Clock, 
    Package, RefreshCw, ArrowRightLeft, ShieldAlert
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const ReguMP_PSF_Attente = () => {
    const [regularisations, setRegularisations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);

    // --- ÉTAT DU SYSTÈME DE NOTIFICATION TOAST ---
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', 
        message: '',
        onConfirm: null
    });

    const primaryColor = '#b45309'; 
    const borderColor = '#e2e8f0';

    // --- RÉCUPÉRATION DES DONNÉES ---
    const fetchRegularisations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/verification-magasin/production/regularisations-attente');
            setRegularisations(res.data || []);
        } catch (err) {
            console.error("Erreur fetch régularisations:", err);
            showToast("Erreur lors de la récupération des régularisations en attente.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    // --- GESTION DU TOAST (AUTO-FERMETURE) ---
    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    // --- SOCKET.IO POUR LES MISES À JOUR EN TEMPS RÉEL ---
    useEffect(() => {
        fetchRegularisations();

        if (!socket) return;

        const user = JSON.parse(localStorage.getItem('user'));
        const companyId = user?.company_id || user?.companyId;
        if (companyId) {
            socket.emit('join_company', companyId.toString());
        }

        const handleRefresh = (data) => {
            if (!data) return;
            console.log("📡 Signal reçu sur Régularisation Stock:", data);
            fetchRegularisations();
        };

        socket.on('REFRESH_UI', handleRefresh);
        socket.on('STOCKS_UPDATED', handleRefresh);
        socket.on('FORCE_REFRESH', fetchRegularisations);

        return () => {
            socket.off('REFRESH_UI', handleRefresh);
            socket.off('STOCKS_UPDATED', handleRefresh);
            socket.off('FORCE_REFRESH', fetchRegularisations);
        };
    }, [fetchRegularisations]);

    // --- ACTION DE CLÔTURE / VALIDATION D'UNE RÉGULARISATION ---
    const handleApurerRegularisation = (id) => {
        showConfirm(
            "Confirmer l'apurement de cette régularisation (le stock sera ajusté ou régularisé) ?",
            () => executerApurement(id)
        );
    };

    const executerApurement = async (id) => {
        setProcessingId(id);
        try {
            await API.put(`/verification-magasin/production/regularisations-attente/apurer/${id}`);
            setNotification({ isOpen: false });
            showToast("Régularisation apurée avec succès.");
            fetchRegularisations();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de l'apurement.", "error");
        } finally {
            setProcessingId(null);
        }
    };

    // --- RENDU DU TOAST ---
    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? '#ef4444' : (isConfirm ? primaryColor : '#10b981');

        return (
            <div style={{
                position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 3000, display: 'flex', alignItems: 'center',
                background: 'white', padding: '12px 20px', borderRadius: '15px',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: `1px solid ${borderColor}`,
                minWidth: '400px', transition: 'all 0.3s ease', borderLeft: `8px solid ${color}`
            }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px', flexShrink: 0
                }}>
                    {notification.type === 'error' ? <AlertTriangle size={20} color={color} /> : 
                     isConfirm ? <ShieldAlert size={20} color={color} /> : 
                     <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={notification.onConfirm} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>
                                OUI, APURER
                            </button>
                            <button onClick={() => setNotification({isOpen: false})} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                        <X size={20} />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', background: '#f1f5f9', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative' }}>
            <Sidebar />
            <NotificationToast />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <header style={{ padding: '20px 30px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ color: '#0f172a', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '12px', margin: 0, fontSize: '20px' }}>
                        <ArrowRightLeft size={28} color={primaryColor} strokeWidth={2.5} /> RÉGULARISATIONS DE STOCK EN ATTENTE (MP / PSF)
                    </h2>
                    <button onClick={fetchRegularisations} style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, padding: '8px 16px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#475569' }}>
                        <RefreshCw size={16} /> Actualiser
                    </button>
                </header>

                <main style={{ flex: 1, padding: '25px', overflowY: 'auto' }}>
                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '20px' }}>
                        
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                                <Loader2 className="animate-spin" color={primaryColor} size={40} />
                            </div>
                        ) : regularisations.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#94a3b8' }}>
                                <Package size={64} strokeWidth={1.5} />
                                <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '16px' }}>Aucune régularisation en attente</p>
                                <p style={{ fontSize: '13px', margin: '5px 0 0 0' }}>Tous les écarts de stock de production ont été régularisés.</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>
                                            <th style={{ padding: '15px' }}>ID RÉFÉRENCE</th>
                                            <th style={{ padding: '15px' }}>ARTICLE (MP / PSF)</th>
                                            <th style={{ padding: '15px' }}>QTÉ MANQUANTE</th>
                                            <th style={{ padding: '15px' }}>ORDRE CONCERNÉ</th>
                                            <th style={{ padding: '15px' }}>STATUT</th>
                                            <th style={{ padding: '15px', textAlign: 'right' }}>ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {regularisations.map(item => (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                                                <td style={{ padding: '15px', color: '#94a3b8', fontWeight: '700' }}>#{item.id}</td>
                                                <td style={{ padding: '15px', fontWeight: '800', color: '#1e293b' }}>{item.article_nom || item.matiere_nom || item.psf_nom || 'Article inconnu'}</td>
                                                <td style={{ padding: '15px', fontWeight: '900', color: primaryColor }}>
                                                    {item.quantite_manquante} {item.unite || ''}
                                                </td>
                                                <td style={{ padding: '15px', color: '#475569' }}>#{item.reference_vente ? item.reference_vente.toString().slice(-8) : 'N/A'}</td>
                                                <td style={{ padding: '15px' }}>
                                                    <span style={{ fontWeight: '800', color: '#d97706', background: '#fef3c7', padding: '4px 10px', borderRadius: '20px', fontSize: '11px' }}>
                                                        {item.statut || 'EN_ATTENTE'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '15px', textAlign: 'right' }}>
                                                    <button 
                                                        disabled={processingId === item.id}
                                                        onClick={() => handleApurerRegularisation(item.id)}
                                                        style={{ background: '#16a34a', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
                                                    >
                                                        {processingId === item.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} 
                                                        APURER / RÉGULARISER
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                    </div>
                </main>
            </div>
        </div>
    );
};

export default ReguMP_PSF_Attente;